import { createContext, useContext, useState, useEffect } from "react";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Get initial theme from localStorage or system preference
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem("myhairchair_theme");
    if (savedTheme) return savedTheme;
    
    // Check system preference
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches) {
      return "light";
    }
    return "dark"; // Default to dark
  };

  const [theme, setTheme] = useState(getInitialTheme);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    if (theme === "light") {
      root.classList.remove("dark");
      root.classList.add("light");
      // Use HSL values to match Tailwind CSS variable format
      root.style.setProperty("--background", "30 6% 98%"); // Light gray
      root.style.setProperty("--foreground", "20 14.3% 4.1%"); // Dark text
      root.style.setProperty("--card", "0 0% 100%"); // Pure white cards
      root.style.setProperty("--card-foreground", "20 14.3% 4.1%");
      root.style.setProperty("--popover", "0 0% 100%");
      root.style.setProperty("--popover-foreground", "20 14.3% 4.1%");
      root.style.setProperty("--primary", "38 92% 50%"); // Amber
      root.style.setProperty("--primary-foreground", "0 0% 100%");
      root.style.setProperty("--secondary", "30 6% 96%");
      root.style.setProperty("--secondary-foreground", "24 9.8% 10%");
      root.style.setProperty("--muted", "30 6% 96%");
      root.style.setProperty("--muted-foreground", "25 5.3% 44.7%");
      root.style.setProperty("--accent", "30 6% 96%");
      root.style.setProperty("--accent-foreground", "24 9.8% 10%");
      root.style.setProperty("--border", "20 5.9% 90%");
      root.style.setProperty("--input", "20 5.9% 90%");
      root.style.setProperty("--ring", "38 92% 50%");
    } else {
      root.classList.remove("light");
      root.classList.add("dark");
      root.style.setProperty("--background", "20 14.3% 4.1%"); // Dark
      root.style.setProperty("--foreground", "60 9.1% 97.8%"); // Light text
      root.style.setProperty("--card", "24 9.8% 10%"); // Stone-800
      root.style.setProperty("--card-foreground", "60 9.1% 97.8%");
      root.style.setProperty("--popover", "24 9.8% 10%");
      root.style.setProperty("--popover-foreground", "60 9.1% 97.8%");
      root.style.setProperty("--primary", "38 92% 50%"); // Amber
      root.style.setProperty("--primary-foreground", "20 14.3% 4.1%");
      root.style.setProperty("--secondary", "12 6.5% 15.1%"); // Stone-700
      root.style.setProperty("--secondary-foreground", "60 9.1% 97.8%");
      root.style.setProperty("--muted", "12 6.5% 15.1%");
      root.style.setProperty("--muted-foreground", "24 5.4% 63.9%");
      root.style.setProperty("--accent", "12 6.5% 15.1%");
      root.style.setProperty("--accent-foreground", "60 9.1% 97.8%");
      root.style.setProperty("--border", "12 6.5% 15.1%");
      root.style.setProperty("--input", "12 6.5% 15.1%");
      root.style.setProperty("--ring", "38 92% 50%");
    }
    
    // Save to localStorage
    localStorage.setItem("myhairchair_theme", theme);
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: light)");
    const handleChange = (e) => {
      // Only auto-switch if user hasn't set a preference
      if (!localStorage.getItem("myhairchair_theme")) {
        setTheme(e.matches ? "light" : "dark");
      }
    };
    
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const toggleTheme = () => {
    setTheme(prev => prev === "dark" ? "light" : "dark");
  };

  const setLightMode = () => setTheme("light");
  const setDarkMode = () => setTheme("dark");
  const setSystemTheme = () => {
    localStorage.removeItem("myhairchair_theme");
    const systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    setTheme(systemPrefersDark ? "dark" : "light");
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      setTheme, 
      toggleTheme, 
      setLightMode, 
      setDarkMode,
      setSystemTheme,
      isDark: theme === "dark",
      isLight: theme === "light"
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
